import React, { useState, useEffect } from 'react';
import LoadingScreen from '../components/ui/LoadingScreen';
import { useParams, useNavigate } from 'react-router-dom';
import { Music2, Plus, ArrowLeft, LogOut } from 'lucide-react';
import { toast } from 'sonner';
import SuggestionList from '../components/guest/SuggestionList';
import SuggestionForm from '../components/guest/SuggestionForm';
import useGuestSession from '../hooks/useGuestSession';
import useGuestPolling from '../hooks/useGuestPolling';

const GuestDashboard = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const { session, loading: sessionLoading, clearSession } = useGuestSession(eventId);
  const [showForm, setShowForm] = useState(false);
  const { suggestions, loading: suggestionsLoading, refreshSuggestions } = useGuestPolling(session?.guestId);

  useEffect(() => {
    // Only redirect if NOT loading and no session
    if (!sessionLoading && !session) {
      navigate(`/guest/${eventId}/join`);
    }
  }, [session, sessionLoading, eventId, navigate]);

  const handleLogout = () => {
    clearSession();
    toast.success('À bientôt ! 👋');
    navigate(`/guest/${eventId}/join`);
  };

  const handleSuggestionAdded = () => {
    setShowForm(false);
    refreshSuggestions();
    toast.success('Suggestion envoyée au DJ ! 🎵');
  };

  if (sessionLoading) {
    return <LoadingScreen message="Connexion à la soirée..." />;
  }

  if (!session) {
    return null;
  }

  const canAddMore = (session.suggestionsCount || suggestions.length) < 5;
  const loading = suggestionsLoading;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <div className="bg-slate-800/30 backdrop-blur-md border-b border-slate-700/50 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-orange-500 to-coral-500 rounded-lg">
                <Music2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">DJ Nomad</h1>
                <p className="text-sm text-slate-400">Espace Invité</p>
              </div>
            </div>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 px-4 py-2 text-slate-400 hover:text-white hover:bg-slate-700/50 rounded-lg transition"
            >
              <LogOut className="w-4 h-4" />
              <span className="text-sm">Quitter</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Welcome Card */}
        <div className="bg-gradient-to-r from-orange-500/10 to-coral-500/10 border border-orange-500/20 rounded-2xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-2">
            Bienvenue {session.guestName} ! 👋
          </h2>
          <p className="text-slate-300">
            Suggérez vos pistes favorites et suivez leur statut en temps réel
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-8">
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 text-center">
            <div className="text-3xl font-bold text-orange-500">{suggestions.length}</div>
            <div className="text-sm text-slate-400 mt-1">Suggestions</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 text-center">
            <div className="text-3xl font-bold text-green-500">
              {suggestions.filter(s => s.status === 'accepted').length}
            </div>
            <div className="text-sm text-slate-400 mt-1">Acceptées</div>
          </div>
          <div className="bg-slate-800/50 backdrop-blur-sm rounded-xl border border-slate-700/50 p-4 text-center">
            <div className="text-3xl font-bold text-yellow-500">
              {suggestions.filter(s => s.status === 'pending').length}
            </div>
            <div className="text-sm text-slate-400 mt-1">En attente</div>
          </div>
        </div>

        {/* Add Suggestion Button */}
        <div className="mb-6">
          <button
            onClick={() => setShowForm(true)}
            disabled={!canAddMore}
            className="w-full flex items-center justify-center gap-2 py-4 px-6 bg-gradient-to-r from-orange-500 to-coral-500 text-white font-semibold rounded-xl hover:from-orange-600 hover:to-coral-600 disabled:opacity-50 disabled:cursor-not-allowed transition shadow-lg hover:shadow-orange-500/25"
          >
            <Plus className="w-5 h-5" />
            <span>{canAddMore ? 'Suggérer une piste' : 'Limite atteinte (5 max)'}</span>
          </button>
          {!canAddMore && (
            <p className="text-center text-slate-400 text-sm mt-2">
              Vous avez atteint la limite de 5 suggestions
            </p>
          )}
        </div>

        {/* Suggestions List */}
        <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
          <h3 className="text-xl font-bold text-white mb-4">Mes Suggestions</h3>
          <SuggestionList suggestions={suggestions} loading={loading} />
        </div>
      </div>

      {/* Suggestion Form Modal */}
      {showForm && (
        <SuggestionForm
          guestId={session.guestId}
          eventId={eventId}
          onClose={() => setShowForm(false)}
          onSuccess={handleSuggestionAdded}
        />
      )}
    </div>
  );
};

export default GuestDashboard;
